import { useTranslation } from "react-i18next"
import { ColorSampleResult } from "@/core/color-sampler"
import { Icon } from "../icons/Icon"
import { toFixed } from "@/utils/math"

export interface ColorPickerProps {
	result: ColorSampleResult | null
	x: number
	y: number
}

export const PixelGridColorPickerPreview = (props: ColorPickerProps) => {
	const { t } = useTranslation("components")
	const x = props.x
	const y = props.y

	const color = props.result?.colorRelative ?? [0, 0, 0]

	const gridDimension = props.result?.size ?? 5
	const gridColors = props.result?.previewData

	const renderGridColor = (index: number) => {
		if (!gridColors) {
			return "rgb(0, 0, 0)"
		}

		const bytesPerPixel = 4
		const x = index % gridDimension
		const y = Math.floor(index / gridDimension)
		const componentIndex = (y * gridDimension + x) * bytesPerPixel

		const r = gridColors[componentIndex] ?? 0
		const g = gridColors[componentIndex + 1] ?? 0
		const b = gridColors[componentIndex + 2] ?? 0

		return `rgba(${r}, ${g}, ${b}, 1.0)`
	}

	const renderGrid = () => {
		return (
			<div className="my-2 grid grid-cols-5">
				{Array(gridDimension * gridDimension)
					.fill(0)
					.map((_, index) => {
						const gridCenter = Math.floor(gridDimension / 2)
						const isCentral = index === gridCenter * gridDimension + gridCenter

						return (
							<div
								key={`color-picker-grid-${index}`}
								style={{ backgroundColor: renderGridColor(index) }}
								className="flex aspect-square size-full items-center justify-center border-[0.5px] border-black"
							>
								{isCentral && <Icon name="center_mark" className="size-2" />}
							</div>
						)
					})}
			</div>
		)
	}

	const renderColorInformation = () => {
		return (
			<div className="flex whitespace-nowrap text-base-400">
				<span className="flex-1 text-left">
					{t("colorPickerPreview.r")}{" "}
					<strong className="text-base-100">{toFixed(color[0] * 100, 1)}</strong>
				</span>
				<span className="flex-1 text-left">
					{t("colorPickerPreview.g")}{" "}
					<strong className="text-base-100">{toFixed(color[1] * 100, 1)}</strong>
				</span>
				<span className="flex-1 text-left">
					{t("colorPickerPreview.b")}{" "}
					<strong className="text-base-100">{toFixed(color[2] * 100, 1)}</strong>
				</span>
				<span>%</span>
			</div>
		)
	}

	return (
		<div
			style={{
				left: x + "px",
				top: y + "px",
			}}
			className="fixed z-[5000] flex w-44 flex-col justify-center overflow-hidden rounded bg-base-800 p-2 text-center text-xxs"
		>
			<h5>{t("colorPickerPreview.pickTargetNeutral")}</h5>
			{renderGrid()}
			{renderColorInformation()}
		</div>
	)
}

export const RoundedColorPickerPreview = (props: ColorPickerProps) => {
	const x = props.x
	const y = props.y

	const imageUrl = props.result?.preview ? URL.createObjectURL(props.result?.preview) : undefined

	return (
		<div
			style={{
				left: x + "px",
				top: y + "px",
			}}
			className="fixed z-[5000] flex size-36 items-center justify-center overflow-hidden rounded-full border-[1.5px] border-white bg-base-800"
		>
			<img src={imageUrl} className="z-9 absolute inset-0 size-36 object-fill" />
			<Icon name="center_mark" className="relative z-10 size-4" />
		</div>
	)
}
